import Configs from '@/Configs';
import { fetchOptions, handleResponse } from '@/common/apiCalls';

/**
 * @param {Response} response
 * @returns {Promise<{ status: number, reason: string | null }>}
 */
async function readAuthenticatedSurveyError(response) {
  const status = response?.status ?? 500;
  try {
    const body = await response.json();
    const reason = typeof body?.reason === 'string' ? body.reason : null;
    return { status, reason };
  } catch {
    return { status, reason: null };
  }
}

/**
 * Load live survey for the signed-in employee (HR shareable URL).
 * @param {string|number} companyID
 * @param {string|number} employeeID
 * @param {string} token
 * @param {string} campaignID
 * @param {string|null} [correlationId]
 * @returns {Promise<unknown[] | number | { status: number, reason: string | null }>}
 */
export async function authenticatedGetPulseSurvey(
  companyID,
  employeeID,
  token,
  campaignID,
  correlationId = null
) {
  try {
    const url =
      Configs.platformAuthenticatedPulseGetSurveyAPI +
      '?companyID=' +
      encodeURIComponent(String(companyID)) +
      '&employeeID=' +
      encodeURIComponent(String(employeeID)) +
      '&campaignID=' +
      encodeURIComponent(String(campaignID));

    const response = await fetch(url, fetchOptions('GET', null, token, 'json', correlationId));
    if (response.status === 200 || response.status === 201) {
      return await handleResponse(response);
    }
    if (response.status === 401) {
      return await handleResponse(response);
    }
    return await readAuthenticatedSurveyError(response);
  } catch (error) {
    console.error(
      '[survey/core/apiCalls.js][authenticatedGetPulseSurvey()] We could not reach the server:',
      error
    );
    return 500;
  }
}

/**
 * Submit multi-question survey answers as the signed-in employee.
 * @param {string|number} companyID
 * @param {string|number} employeeID
 * @param {string} token
 * @param {string} campaignID
 * @param {unknown} answerData
 * @param {string|null} [correlationId]
 * @returns {Promise<unknown | number | { status: number, reason: string | null }>}
 */
export async function authenticatedPostPulseSurveyAnswer(
  companyID,
  employeeID,
  token,
  campaignID,
  answerData,
  correlationId = null
) {
  try {
    const url =
      Configs.platformAuthenticatedPulsePostSurveyAnswersAPI +
      '?companyID=' +
      encodeURIComponent(String(companyID)) +
      '&employeeID=' +
      encodeURIComponent(String(employeeID)) +
      '&campaignID=' +
      encodeURIComponent(String(campaignID));

    const response = await fetch(
      url,
      fetchOptions('POST', answerData, token, 'json', correlationId)
    );
    if (response.status === 200 || response.status === 201) {
      return await handleResponse(response);
    }
    if (response.status === 401) {
      return await handleResponse(response);
    }
    return await readAuthenticatedSurveyError(response);
  } catch (error) {
    console.error(
      '[survey/core/apiCalls.js][authenticatedPostPulseSurveyAnswer()] We could not reach the server:',
      error
    );
    return 500;
  }
}

/**
 * Map authenticated survey API reason → i18n key.
 * @param {string | null | undefined} reason
 * @returns {string}
 */
export function authenticatedSurveyErrorI18nKey(reason) {
  switch (reason) {
    case 'not_in_target':
    case 'wrong_company':
    case 'non_survey_type':
    case 'inactive_or_unknown_employee':
      return 'pulse.survey.notInTarget';
    case 'campaign_closed':
      return 'pulse.survey.campaignClosed';
    case 'campaign_not_found':
      return 'pulse.survey.notFound';
    case 'already_completed':
      return 'pulse.survey.answered';
    default:
      return 'pulse.survey.notEligible';
  }
}
